import { useExpertUniversityConfiguration, useExpertUniversityStore } from "@expert/expert-university";
import { trackFullStoryEvent } from "@expert/monitoring";
import { showNotification } from "@mantine/notifications";
import { useCallback, useEffect, useRef, useState } from "react";

import { useLogger } from "@/sdk/logging/LoggerProvider";

import type {
    EvaluationResultsErrorResponse,
    SessionEvaluationSkippedStatus,
    StoredSessionReadResult,
    TranscriptContent,
} from "../api/evaluations/types";

import { evaluateTrainingSessionBySessionId, getEvaluationResultsBySessionId } from "../api/requests";
import { TrainingSessionChallengeResultStatus, TrainingSessionChallengeStatusEnum } from "../types";
import { isEvaluationTranscriptContent } from "../utils";

// Nothing to render and nothing to poll for: the backend writes no EvaluationStatus for the pre-judge
// gates. `already_completed` is listed to mirror the type, but is handled earlier and never reaches here.
const SKIPPED_STATUSES: readonly SessionEvaluationSkippedStatus[] = ["already_completed", "too_short", "no_transcript"];

const isSkippedStatus = (status: string): status is SessionEvaluationSkippedStatus =>
    (SKIPPED_STATUSES as readonly string[]).includes(status);

const EVALUATION_ERROR_RESPONSE: EvaluationResultsErrorResponse = Object.freeze({
    ErrorCode: "EVALUATION_ERROR",
    Message: "Failed to fetch evaluation results",
});

const MAX_RETRIES = 3;

// A 500 or a client-side timeout does not mean the judge stopped — the server never threads the
// request's AbortSignal into it — so these now mean "go read the persisted state", not "re-evaluate".
const isRetryableError = (result: { status: string; statusCode?: number }) =>
    result.status === "error" ||
    (result.status === "server_error" && result.statusCode !== undefined && result.statusCode >= 500);

const POLL_INTERVAL_MS = 3_000;
// 20s POST + 110s poll = 130s, matching the server's worst case: a 120s judge budget plus the ~10s
// maximum observed non-judge overhead (p50 144ms, p99 767ms, max 10,213ms).
//
// This must stay at or above the server's worst case, and not for tidiness. Giving up early hands the
// expert a retry button while the judge is still running, and a retry in that window starts a *second*
// judge call against the same session: the backend's `IN_PROGRESS` claim is a signal, not a lock, so
// nothing server-side deduplicates concurrent attempts. The cap is therefore the only thing standing
// between a slow evaluation and duplicate LLM spend. It is also the only backstop for a stuck
// `IN_PROGRESS`, which nothing clears if the pod dies mid-judge.
const POLL_CAP_MS = 110_000;

type PolledStatusVerdict = "untracked" | "pending" | "success" | "retryable" | "terminal";

/**
 * Deliberately classifies a bare string rather than narrowing a union: the server passes the stored
 * value through without validating it. An unrecognised value must fall through to
 * `"pending"` so it rides to the poll cap and offers a retry, rather than hanging or dead-ending.
 *
 * `undefined` is `"untracked"` rather than `"pending"`, and that split is load-bearing. Absence can
 * only mean "never touched by the new backend", because the backend writes a status before the judge
 * runs — so a record with no status but a payload is a pre-feature record whose verdict is the only one
 * it will ever have, and it must still render. `IN_PROGRESS` is the opposite case: any payload on that
 * record belongs to a *previous* attempt, so it falls to `"pending"` and keeps polling.
 */
const classifyEvaluationStatus = (status: string | undefined): PolledStatusVerdict => {
    switch (status) {
        case undefined:
            return "untracked";
        case "SUCCESS":
            return "success";
        case "FAILED_RETRYABLE":
            return "retryable";
        case "FAILED_PERMANENT":
            return "terminal";
        default:
            return "pending";
    }
};

type PollOutcome =
    | { outcome: "results"; record: TranscriptContent }
    | { outcome: "retryable" }
    | { outcome: "terminal" }
    /** The cap expired with the record still in progress, absent, or unreadable. */
    | { outcome: "capped" };

type PollResult = PollOutcome & {
    pollTicks: number;
    pollFailedTicks: number;
    /** Failed ticks by HTTP status code; `none` counts the ones that got no response. */
    pollFailedStatusCodes: Record<string, number>;
    pollElapsedMs: number;
};

/** Resolves after `ms`, or immediately once `signal` aborts. */
const delay = (ms: number, signal: AbortSignal) =>
    new Promise<void>((resolve) => {
        if (signal.aborted) {
            resolve();
            return;
        }
        const timer = setTimeout(resolve, ms);
        signal.addEventListener(
            "abort",
            () => {
                clearTimeout(timer);
                resolve();
            },
            { once: true },
        );
    });

const STORED_READ_MAX_ATTEMPTS = 3;
const STORED_READ_RETRY_DELAY_MS = 500;

/** The loop below owns the retry budget, so each attempt must be one short request, never three long ones. */
const readStoredOnce = (sessionId: string) => getEvaluationResultsBySessionId(sessionId, { isStoredReadAttempt: true });

/**
 * A one-shot stored read with its own small retry budget, shared by the bare `already_completed` branch
 * and `viewResultsBySessionId`.
 *
 * `already_completed` means the verdict is already persisted, so a `failed` read there is a transport
 * blip by definition — the one case where retrying is guaranteed to be reading something that exists.
 * It needs a dedicated path because `already_completed` is not an `isRetryableError`, so the outer retry
 * branch cannot reach it, and sending it to the poller would be wrong in kind: there is nothing to wait
 * for, only a flaky read to repeat.
 *
 * Retries `failed` only. `absent` is an answer — the record is not there — and repeating the read will
 * not conjure it.
 */
async function readStoredEvaluationWithRetry(sessionId: string, signal: AbortSignal): Promise<StoredSessionReadResult> {
    let result = await readStoredOnce(sessionId);

    for (let attempt = 2; attempt <= STORED_READ_MAX_ATTEMPTS; attempt++) {
        if (result.status !== "failed" || signal.aborted) break;
        await delay(STORED_READ_RETRY_DELAY_MS, signal);
        if (signal.aborted) break;
        result = await readStoredOnce(sessionId);
    }

    return result;
}

/**
 * **Must only start after the evaluate POST has given up — never at t=0.** A re-evaluatable session
 * (`ChallengeStatus: NOT COMPLETED`) still carries the *previous* attempt's `SUCCESS` and stale
 * `EvaluationDetails` until the backend overwrites the status with `IN_PROGRESS` ~150ms in; a poll
 * racing that write reads a stale `SUCCESS` and renders the expert's old verdict as this call's. The
 * 20s POST timeout removes that race structurally.
 *
 * Starting late is necessary but not sufficient. Once the status *is* `IN_PROGRESS`, the previous
 * attempt's `EvaluationDetails` is still sitting on the stored item — overwriting the status does not
 * clear it. The server withholds it from the read until the status is `SUCCESS`, and classifying the
 * status before looking at the payload keeps that guarantee on this side too, as defence in depth.
 */
async function pollForPersistedEvaluation(sessionId: string, signal: AbortSignal): Promise<PollResult> {
    const startedAt = Date.now();
    const deadline = startedAt + POLL_CAP_MS;
    let pollTicks = 0;
    let pollFailedTicks = 0;
    const pollFailedStatusCodes: Record<string, number> = {};
    const finish = (outcome: PollOutcome): PollResult => ({
        ...outcome,
        pollTicks,
        pollFailedTicks,
        pollFailedStatusCodes,
        pollElapsedMs: Date.now() - startedAt,
    });

    while (!signal.aborted && Date.now() < deadline) {
        const read = await getEvaluationResultsBySessionId(sessionId, { isPollTick: true });
        pollTicks += 1;
        if (read.status === "failed") {
            pollFailedTicks += 1;
            // No status code means no response at all: a network error or a timeout.
            const key = String(read.statusCode ?? "none");
            pollFailedStatusCodes[key] = (pollFailedStatusCodes[key] ?? 0) + 1;
        }
        if (signal.aborted) break;

        if (read.status === "found") {
            // Status BEFORE payload. A re-evaluation overwrites the status with `IN_PROGRESS` but does
            // not clear `EvaluationDetails`, so the stored item can hold a real verdict belonging to the
            // previous attempt. The server withholds it; this check must not rely on that alone.
            const verdict = classifyEvaluationStatus(read.item.EvaluationStatus);
            if (verdict === "retryable") return finish({ outcome: "retryable" });
            if (verdict === "terminal") return finish({ outcome: "terminal" });
            // Only these two may be read off the item: `success` is this call's verdict, and
            // `untracked` is a pre-feature record whose payload is the only verdict it will ever carry.
            if ((verdict === "success" || verdict === "untracked") && isEvaluationTranscriptContent(read.item)) {
                return finish({ outcome: "results", record: read.item });
            }
            // Everything else keeps polling: `pending` (`IN_PROGRESS` or an unrecognised value) whatever
            // the payload holds, and a `SUCCESS` whose payload has not landed yet. The latter needs only
            // eventual consistency to explain it — the backend writes details and `SUCCESS` in one
            // `UpdateItem`, so a durably empty verdict under `SUCCESS` is not a state it can produce.
        }
        // `absent` and `failed` both mean "no answer yet", never "no results".

        await delay(POLL_INTERVAL_MS, signal);
    }

    return finish({ outcome: "capped" });
}

/**
 * Discriminates *why* the hook is in a pending state so consumers can render different interim UI:
 * - `"evaluating"`: the post-call `trigger-evaluation-results` flow — the evaluate POST is in flight
 *   (`session/{sessionId}/evaluate`).
 * - `"scoring"`: the same flow, after the POST returned no verdict and the hook started polling. A real
 *   signal, not a timer: reaching it means the pre-judge gates passed and the judge is running.
 * - `"loading"`: the `viewResultsBySessionId` flow — reading a previously-stored result.
 */
export type EvaluationPendingReason = "evaluating" | "scoring" | "loading";

export interface UseTrainingEvaluationResults {
    evaluationResults: TranscriptContent | EvaluationResultsErrorResponse | undefined;
    /**
     * Backward-compatible boolean pending flag, derived as `pendingReason !== undefined`. True while
     * either the post-call evaluation or the stored-result read is in flight.
     */
    isPending: boolean;
    /**
     * Why the hook is pending, or `undefined` when idle. The UI renders a "call complete" interstitial
     * for `"evaluating"` (a fresh evaluation is being generated after a call ends) but a plain loading
     * state for `"loading"` (reading a previously-stored result).
     */
    pendingReason: EvaluationPendingReason | undefined;
    hasRetryableError: boolean;
    retryEvaluation: () => void;
    clearEvaluationResults: () => void;
    /**
     * Loads the already-stored evaluation for a past session (the tile's "View results" click) and
     * drives the same loading/results/error state as the live post-call flow. This is distinct from
     * the `session/{sessionId}/evaluate` endpoint the mount-time listener uses: that generates a fresh
     * evaluation on hangup, whereas this reads a persisted result and never re-triggers evaluation.
     */
    viewResultsBySessionId: (sessionId: string) => Promise<void>;
}

export interface UseTrainingEvaluationResultsOptions {
    /**
     * Limits the hook to the profiles it returns `true` for; every other `trigger-evaluation-results`
     * event is left untouched for the caller's own listener. Omit to handle every profile.
     */
    handlesProfile?: (profileId: string | undefined) => boolean;
    /** Logger module the caller's events are attributed to. */
    module?: string;
    /** Stamped on every log line as `isAscend`; only the ASCEND learning-blocks experience is ASCEND. */
    isAscend?: boolean;
}

/**
 * Owns the `session/{sessionId}/evaluate` evaluation-results flow: it listens for the
 * `trigger-evaluation-results` event, calls the endpoint, and drives the loading/results/error state its
 * caller renders. `ExpertUniversityLearningBlocks` uses it for every profile; `ExpertUniversityHome` uses
 * it only for the ATT Guided Browsing profiles, via `handlesProfile`. Mirrors the FullStory + logger events of the
 * legacy flow on a completed evaluation. The endpoint already persists the evaluation to the
 * `ai-assisted-training` DS record server-side, so this hook does not write the training session from
 * the client.
 */
export function useTrainingEvaluationResults({
    handlesProfile,
    module = "Expert University Learning Blocks",
    isAscend = true,
}: UseTrainingEvaluationResultsOptions = {}): UseTrainingEvaluationResults {
    const logger = useLogger({ module });
    const { taskInSandbox } = useExpertUniversityConfiguration();

    const [evaluationResults, setEvaluationResults] = useState<
        TranscriptContent | EvaluationResultsErrorResponse | undefined
    >();
    // Seed from the store's `isEvaluationPending` flag, which the SDK sets in `hangupCall` before the
    // task-completion that remounts this component. Seeding lazily means the interstitial is on screen
    // on the very first render — no flash of the default learning-blocks view while we wait for the
    // `trigger-evaluation-results` event. Sandbox 'calls' produce no evaluation, so never seed there.
    const [pendingReason, setPendingReason] = useState<EvaluationPendingReason | undefined>(() => {
        const { isEvaluationPending, evaluationPendingProfileId } = useExpertUniversityStore.getState();
        const isOwnPendingEvaluation = handlesProfile?.(evaluationPendingProfileId) ?? true;
        return !taskInSandbox && isEvaluationPending && isOwnPendingEvaluation ? "evaluating" : undefined;
    });
    const [hasRetryableError, setHasRetryableError] = useState(false);

    // The subscription effect runs once (empty deps), so read the latest sandbox flag through a ref
    // rather than capturing the mount-time value in the async handler's closure.
    const taskInSandboxRef = useRef(taskInSandbox);
    taskInSandboxRef.current = taskInSandbox;
    const handlesProfileRef = useRef(handlesProfile);
    handlesProfileRef.current = handlesProfile;

    const retryCountRef = useRef(0);
    const lastEventRef = useRef<{ sessionId: string; profileId: string } | undefined>(undefined);
    // A poll outlives a mount, so a late tick could otherwise write state or burn a retry on its way out.
    const pollAbortRef = useRef<AbortController | undefined>(undefined);
    // The stored read retries, so it can outlive its mount for the same reason.
    const storedReadAbortRef = useRef<AbortController | undefined>(undefined);

    function finishWithResults(
        record: TranscriptContent,
        sessionId: string,
        profileId: string,
        source: "response" | "poll" | "stored",
    ) {
        // The freshly-triggered evaluation response may omit ChallengeId. Fall back to the
        // profileId from the event so the updated-call-flow checklist resolves correctly.
        record.ChallengeId ??= profileId;

        const evaluationOutcome = record.EvaluationDetails.Results?.toLocaleLowerCase();
        const challengeStatus =
            evaluationOutcome === TrainingSessionChallengeResultStatus.CLEARED
                ? TrainingSessionChallengeStatusEnum.COMPLETE
                : TrainingSessionChallengeStatusEnum.NOT_COMPLETE;

        // The new endpoint's EvaluationDetails omits the `logs` block, so behaviorsPresent /
        // maxBehaviors are unavailable here and are intentionally not reported.
        trackFullStoryEvent("Evaluation Results Received", {
            sessionId,
            profileId,
            evaluationResults: evaluationOutcome,
        });
        logger.info(
            {
                challengeStatus,
                evaluationResults: evaluationOutcome,
                sessionId,
                profileId,
                isAscend,
                // The only way to measure how often the short POST timeout is handing off to the poll.
                evaluationSource: source,
            },
            "Triggering evaluation results",
        );

        setEvaluationResults(record);
        setPendingReason(undefined);
    }

    /** Terminal failure: the error overlay, which offers "Return Home" and deliberately no retry. */
    function failEvaluation(sessionId: string, profileId: string, details: Record<string, unknown>) {
        logger.error({ ...details, sessionId, isAscend }, "Error triggering evaluation results");
        trackFullStoryEvent("Evaluation Results Error", {
            sessionId,
            profileId,
        });

        setEvaluationResults(EVALUATION_ERROR_RESPONSE);
        setPendingReason(undefined);
    }

    async function processEvaluation(sessionId: string, profileId: string) {
        // Supersede any poll still running from an earlier attempt before starting a new one.
        pollAbortRef.current?.abort();
        const pollAbort = new AbortController();
        pollAbortRef.current = pollAbort;

        try {
            const result = await evaluateTrainingSessionBySessionId(sessionId);

            // `already_completed` is a success: it replays a verdict already computed and stored, which
            // is exactly the expert whose earlier HTTP response was lost.
            if (
                (result.status === "completed" || result.status === "already_completed") &&
                isEvaluationTranscriptContent(result)
            ) {
                finishWithResults(result, sessionId, profileId, "response");
                return;
            }

            // A bare `already_completed` — the endpoint's payload spread is not deployed yet, or the
            // record was marked COMPLETED without one. The session is already COMPLETED, so the verdict
            // exists *now*: this is a one-shot read, never a poll, because there is nothing to wait for.
            // Without it a bare replay reaches the toast below and tells an expert who passed that they
            // have no results.
            if (result.status === "already_completed") {
                logger.info({ sessionId, isAscend }, "Evaluation already completed; fetching stored result");
                // Bounded retry on the read: the verdict exists, so a transport failure here must not
                // tell an expert who passed that they have no results.
                const stored = await readStoredEvaluationWithRetry(sessionId, pollAbort.signal);
                // Unmounted or superseded while retrying: whoever aborted us owns the state now.
                if (pollAbort.signal.aborted) return;
                if (stored.status === "found" && isEvaluationTranscriptContent(stored.item)) {
                    finishWithResults(stored.item, sessionId, profileId, "stored");
                    return;
                }
                // `readStatus` stays on the line: it separates a record that is genuinely absent from a
                // read that still would not complete after its retries.
                failEvaluation(sessionId, profileId, { evaluationStatus: result.status, readStatus: stored.status });
                return;
            }

            // 409: the attempt cap is spent, so nothing was attempted and a retry would 409 again. The
            // counts are read off the body because the cap lives on the backend.
            if (result.status === "retry_exhausted") {
                failEvaluation(sessionId, profileId, {
                    evaluationStatus: result.status,
                    evaluationAttempts: result.attempts,
                    evaluationMaxAttempts: result.maxAttempts,
                });
                return;
            }

            // The judge is probably still running server-side, so read the state it persists.
            let pollOutcome: PollOutcome["outcome"] | undefined;
            if (isRetryableError(result)) {
                setPendingReason("scoring");
                const polled = await pollForPersistedEvaluation(sessionId, pollAbort.signal);
                // Unmounted or superseded mid-poll: whoever aborted us owns the state now.
                if (pollAbort.signal.aborted) return;
                pollOutcome = polled.outcome;
                // Only a verdict is a clean finish; every other outcome leaves the expert at an error or a retry.
                logger[pollOutcome === "results" ? "info" : "warn"](
                    {
                        sessionId,
                        isAscend,
                        pollOutcome,
                        pollTicks: polled.pollTicks,
                        pollFailedTicks: polled.pollFailedTicks,
                        pollFailedStatusCodes: polled.pollFailedStatusCodes,
                        pollElapsedMs: polled.pollElapsedMs,
                    },
                    "Evaluation poll finished",
                );

                if (polled.outcome === "results") {
                    finishWithResults(polled.record, sessionId, profileId, "poll");
                    return;
                }
                if (polled.outcome === "terminal") {
                    failEvaluation(sessionId, profileId, { evaluationStatus: result.status, pollOutcome });
                    return;
                }
                // "retryable" and "capped" fall through to the MAX_RETRIES branch below; the poll
                // deliberately runs no competing retry budget.
            }

            if (isSkippedStatus(result.status)) {
                logger.info(
                    { evaluationStatus: result.status, sessionId, isAscend },
                    "Evaluation skipped; no results to display",
                );
                showNotification({
                    id: "evaluation-skipped",
                    position: "top-center",
                    withCloseButton: true,
                    autoClose: 4000,
                    message: "No evaluation results are available for this session.",
                });
                setEvaluationResults(undefined);
                setPendingReason(undefined);
                return;
            }

            if (isRetryableError(result)) {
                if (retryCountRef.current > 0) {
                    logger.warn(
                        {
                            retryCount: retryCountRef.current,
                            sessionId,
                            isAscend,
                            pollOutcome,
                        },
                        "Retrying evaluation results",
                    );
                }
                if (retryCountRef.current < MAX_RETRIES) {
                    retryCountRef.current += 1;
                    setHasRetryableError(true);
                    setPendingReason("evaluating");
                    return;
                }
            }
            failEvaluation(sessionId, profileId, {
                error: "error" in result ? result.error : undefined,
                evaluationStatus: result.status,
                pollOutcome,
            });
        } catch (error: any) {
            if (retryCountRef.current < MAX_RETRIES) {
                retryCountRef.current += 1;
                setHasRetryableError(true);
                // A throw during the poll must not leave the interstitial still claiming to be scoring.
                setPendingReason("evaluating");
                logger.warn({ error: error?.message, sessionId, isAscend }, "Error triggering evaluation results");
                return;
            }
            failEvaluation(sessionId, profileId, { error: error?.message });
        }
    }

    useEffect(() => {
        // Consume the seed flag once: we've already read it into `pendingReason` above, so clear it here
        // so a later remount (without a fresh call) doesn't re-open the interstitial. The live flow from
        // here on is driven entirely by the `trigger-evaluation-results` event and local state.
        if (useExpertUniversityStore.getState().isEvaluationPending) {
            useExpertUniversityStore.getState().setEvaluationPending(false);
        }

        const eventBus = useExpertUniversityStore.getState().eventBus;
        const off = eventBus?.on("trigger-evaluation-results", async (event) => {
            if (handlesProfileRef.current && !handlesProfileRef.current(event.profileId)) return;
            // After ending a 'call' in the sandbox environment there are no evaluation results to fetch.
            if (taskInSandboxRef.current) {
                setEvaluationResults(undefined);
                return;
            }
            lastEventRef.current = { sessionId: event.sessionId, profileId: event.profileId };
            retryCountRef.current = 0;
            setHasRetryableError(false);
            setPendingReason("evaluating");

            await processEvaluation(event.sessionId, event.profileId);
        });

        return () => {
            off?.();
            pollAbortRef.current?.abort();
            storedReadAbortRef.current?.abort();
        };
        // oxlint-disable-next-line exhaustive-deps
    }, []);

    const retryEvaluation = useCallback(() => {
        const event = lastEventRef.current;
        if (!event) return;
        setHasRetryableError(false);
        setPendingReason("evaluating");
        void processEvaluation(event.sessionId, event.profileId);
        // oxlint-disable-next-line exhaustive-deps
    }, []);

    const clearEvaluationResults = useCallback(() => {
        setEvaluationResults(undefined);
    }, []);

    const viewResultsBySessionId = useCallback(
        async (sessionId: string) => {
            storedReadAbortRef.current?.abort();
            const readAbort = new AbortController();
            storedReadAbortRef.current = readAbort;

            setPendingReason("loading");
            try {
                // A `failed` read is unknown, not empty, here too — so it gets the same bounded retry.
                const read = await readStoredEvaluationWithRetry(sessionId, readAbort.signal);
                // Unmounted or superseded while retrying: whoever aborted us owns the state now.
                if (readAbort.signal.aborted) return;
                if (read.status === "found" && isEvaluationTranscriptContent(read.item)) {
                    trackFullStoryEvent("Evaluation Results Received", { sessionId });
                    setEvaluationResults(read.item);
                } else {
                    // `readStatus` separates the three non-renderable cases: a `failed` is a bug to
                    // chase, `absent` and a verdict-less record are data.
                    logger.info(
                        { sessionId, isAscend, readStatus: read.status },
                        "No stored evaluation results found for session",
                    );
                    trackFullStoryEvent("Evaluation Results Error", { sessionId });
                    setEvaluationResults(EVALUATION_ERROR_RESPONSE);
                }
            } catch (error: any) {
                // Defensive: the read reports failures as a result rather than throwing, but a malformed
                // response would otherwise reject a discarded promise silently.
                logger.error(
                    { error: error?.message, sessionId, isAscend },
                    "Failed to load stored evaluation results",
                );
                trackFullStoryEvent("Evaluation Results Error", { sessionId });
                setEvaluationResults(EVALUATION_ERROR_RESPONSE);
            } finally {
                if (!readAbort.signal.aborted) setPendingReason(undefined);
            }
        },
        [logger, isAscend],
    );

    const isPending = pendingReason !== undefined;

    return {
        evaluationResults,
        isPending,
        pendingReason,
        hasRetryableError,
        retryEvaluation,
        clearEvaluationResults,
        viewResultsBySessionId,
    };
}
